/**
 * Apple Sign In Module
 * Handles Sign in with Apple authentication on iOS
 */

import { isNativePlatform, getEnvironment } from '../config';
import { success as hapticSuccess } from '../utils/haptics';

// Check if Sign in with Apple is available (iOS only)
export function isAppleSignInAvailable(): boolean {
  if (!isNativePlatform()) return false;

  // Check if we're on iOS
  const platform = (window as any).Capacitor?.getPlatform?.();
  return platform === 'ios';
}

export interface AppleAuthResult {
  user: string;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  identityToken: string;
  authorizationCode: string;
}

/**
 * Trigger Sign in with Apple flow
 * Returns the user's credentials or throws on cancellation/error
 */
export async function signInWithApple(): Promise<AppleAuthResult> {
  if (!isAppleSignInAvailable()) {
    throw new Error('Sign in with Apple is not available');
  }

  try {
    const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

    const result = await SignInWithApple.authorize({
      clientId: 'com.twentytwoquestions.app',
      redirectURI: 'https://22questions.com/auth/apple-callback',
      scopes: 'email name',
      state: generateState(),
      nonce: generateNonce(),
    });

    // Trigger haptic feedback on success
    await hapticSuccess();

    return {
      user: result.response.user!,
      email: result.response.email,
      givenName: result.response.givenName,
      familyName: result.response.familyName,
      identityToken: result.response.identityToken,
      authorizationCode: result.response.authorizationCode,
    };
  } catch (err: any) {
    // User cancelled
    if (err?.code === 1001 || err?.message?.includes('cancelled')) {
      throw new Error('Sign in cancelled');
    }
    throw err;
  }
}

/**
 * Generate a random state for CSRF protection
 */
function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a random nonce
 */
function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export default {
  isAppleSignInAvailable,
  signInWithApple,
};
